import React, { forwardRef, useEffect, useState } from "react";
import { api } from "@/lib/api";

const backend = process.env.REACT_APP_BACKEND_URL;

function resolveMedia(src, retry) {
  if (!src || src.startsWith("blob:") || src.startsWith("data:")) return src;
  try {
    const url = new URL(src, backend);
    if (url.origin === new URL(backend).origin &&
        (url.pathname.startsWith("/api/photos/") || url.pathname.startsWith("/api/messages/media/"))) {
      // Avoid reusing responses cached under the former public-media policy.
      url.searchParams.set("access", "private-v1");
      if (retry) url.searchParams.set("retry", String(retry));
      return url.href;
    }
    return src;
  } catch { return src; }
}

function privateElement(tag) {
  return forwardRef(function PrivateMedia({ src, onError, ...props }, ref) {
    const [retriedSrc, setRetriedSrc] = useState(null);
    const [pending, setPending] = useState(false);
    const retry = retriedSrc === src ? 1 : 0;
    useEffect(() => { setPending(false); }, [src]);
    const resolved = resolveMedia(src, retry);
    const error = async (event) => {
      const protectedMedia = resolved && resolved !== src;
      if (protectedMedia && !retry && !pending) {
        setPending(true);
        try {
          // Normal auth interceptor refreshes an expired session. Never put a
          // token in a URL or make the storage public to display an image.
          await api.get("/auth/me");
          setRetriedSrc(src);
        } catch { onError?.(event); }
        finally { setPending(false); }
      } else {
        onError?.(event);
      }
    };
    return React.createElement(tag, { ...props, ref, src: resolved, onError: error });
  });
}

export const PrivateImage = privateElement("img");
export const PrivateAudio = privateElement("audio");
